import { createSlice, createAction } from '@reduxjs/toolkit';

import type { StatusEntity, EntityServiceResponse } from '@models';
import { nullStatusEntity } from '@models';
import { SET_IMAGE_ADD_SUCCESS } from '@store/action-types';
import {
  createEntityAction,
  createStatusEntityReducers,
  getEntityActions,
} from '@store/common';
import { setViewState } from '@store/views.slice';
import type {
  FetchInventoryClustersParams,
  FetchInventoryImagesParams,
  FetchInventoryVulnerabilitiesParams,
  FetchInventoryVulnSummaryParams,
  GetInventoryImagesResponse,
  GetInventoryVulnerabiltiesResponse,
  GetInventoryVulnSummaryResponse,
  InventoryClusterList,
  InventoryImageItem,
  InventoryVulnerabilityList,
  InventoryVulnSummary,
} from '@features/kubernetes/inventory.models';

export interface InventoriesState {
  clusters: StatusEntity<InventoryClusterList>;
  images: StatusEntity<InventoryImageItem[]> & {
    totalRows?: number;
  };
  imagesLastUpdated?: string;
  vulnerabilities: StatusEntity<InventoryVulnerabilityList>;
  vulnSummary: StatusEntity<InventoryVulnSummary>;
}

export const initialState: InventoriesState = {
  clusters: nullStatusEntity,
  images: nullStatusEntity,
  vulnerabilities: nullStatusEntity,
  vulnSummary: nullStatusEntity,
};

const setImagesLastUpdated = createAction<string>(
  'inventories/setImagesLastUpdated',
);

const entities = {
  fetchInventoryClusters: createEntityAction<
    FetchInventoryClustersParams,
    EntityServiceResponse<InventoryClusterList>
  >({
    sliceName: 'inventories',
    stateKey: 'clusters',
    servicePath: () => `/service/inventories/clusters`,
  }),
  fetchInventoryImages: createEntityAction<
    FetchInventoryImagesParams,
    GetInventoryImagesResponse
  >({
    sliceName: 'inventories',
    stateKey: 'images',
    servicePath: () => `/service/inventories/images`,
    customRequest: (request, path, params) =>
      request.get(path).query({
        cluster: params.cluster,
        namespace: params.namespace,
        limit: params.limit,
        filter: params.filter,
        page: params.page,
        sort: params.sort,
        compliant: params.compliant,
        evaluated: params.evaluated,
        severity: params.severity,
      }),
    debounce: true,
    onSuccess: (_, { cluster, namespace }, { dispatch }) => {
      dispatch(
        setViewState('inventoriesImagesTable', {
          cluster,
          namespace,
        }),
      );
    },
  }),
  fetchInventoryVulnerabilities: createEntityAction<
    FetchInventoryVulnerabilitiesParams,
    GetInventoryVulnerabiltiesResponse
  >({
    sliceName: 'inventory',
    stateKey: 'vulnerabilities',
    servicePath: () => `/service/inventories/vulnerabilities`,
    customRequest: (request, path, params) =>
      request
        .get(path)
        .query({ cluster: params.cluster, namespace: params.namespace }),
    onSuccess: (_, { cluster, namespace }, { dispatch }) => {
      dispatch(
        setViewState('inventoriesVulnerabilitiesTable', {
          cluster,
          namespace,
        }),
      );
    },
  }),
  fetchInventoryVulnSummary: createEntityAction<
    FetchInventoryVulnSummaryParams,
    GetInventoryVulnSummaryResponse
  >({
    sliceName: 'inventory',
    stateKey: 'vulnSummary',
    servicePath: () => `/service/inventories/vulnerability_summary`,
    customRequest: (request, path, params) =>
      request
        .get(path)
        .query({ cluster: params.cluster, namespace: params.namespace }),
  }),
};
export const {
  fetchInventoryClusters,
  fetchInventoryVulnerabilities,
  fetchInventoryImages,
  fetchInventoryVulnSummary,
} = getEntityActions(entities);

const inventoriesSlice = createSlice({
  name: 'inventories',
  initialState,
  reducers: {
    clearInventoryImages: state => {
      state.images = {
        ...state.images,
        data: [],
        totalRows: 0,
      };
    },
  },
  extraReducers: builder => {
    createStatusEntityReducers(entities, builder);

    builder
      .addCase(SET_IMAGE_ADD_SUCCESS, state => {
        state.imagesLastUpdated = new Date().toISOString();
      })
      .addCase(setImagesLastUpdated, (state, action) => {
        state.imagesLastUpdated = action.payload;
      });
  },
});

export const { clearInventoryImages } = inventoriesSlice.actions;

export default inventoriesSlice.reducer;
